import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { ConfigService } from '@core/shared/config';
import { AccessService } from '../../access/access/access.service';
import { AppMessage } from '../../messages';
import * as i0 from "@angular/core";
import * as i1 from "@core/shared/config";
import * as i2 from "../../access/access/access.service";
function httpErrorToCustomEvent(err) {
  return {
    message: err.message
    // controls additional data field
    // extra:{ 
    // 	eventData:{error:err.error}
    // }
  };
}
const ERROR_HANDLED = 'ERROR_HANDLED';
const HTTP_STATUS_GROUP_ERROR = [0, 500];
const HTTP_STATUS_GROUP_IGNORE = [400,
// bad request, often caught due to wrong login credentials
503 // maintenance mode 
];
export let ErrorHandlerService = /*#__PURE__*/(() => {
  class ErrorHandlerService {
    constructor(config, access) {
      this.config = config;
      this.access = access;
      this.platformIsAdmin = this.config.isPlatform('admin');
      this.isSSR = isPlatformServer(inject(PLATFORM_ID));
      this.sentryCache = null;
    }
    handleError(incomingError) {
      const error = this.extractInformationFromIncomingData(incomingError);
      if (error instanceof HttpErrorResponse) {
        /**
         * HttpErrorResponse may be sent to NgErrorHandler multiple times.
         * This is nothing we can prevent, so instead we mark already handled errors with ERROR_HANDLED=true.
         * This way we can skip already handled errors and keep log/sentry free of duplicates.
         */
        if (error[ERROR_HANDLED]) return;
        error[ERROR_HANDLED] = true;
        if (HTTP_STATUS_GROUP_IGNORE.includes(error.status)) return;
        if (HTTP_STATUS_GROUP_ERROR.includes(error.status)) return this.logError(error);
        return this.logInfo(error);
      } else if (error) {
        this.logError(error);
      } else {
        // in some cases, error handler will catch something like an AppMessage, which does not actually contain an error.
        // these will end up here, being ignored.
        if (!IS_PRODUCTION) console.log('ErrorHandler ignored incoming error: ', incomingError);
      }
    }
    logError(error) {
      this._handleData(error, 'error');
    }
    logWarning(error) {
      this._handleData(error, 'warning');
    }
    logInfo(error) {
      this._handleData(error, 'info');
    }
    loadSentry() {
      if (this.sentryCache) {
        if (this.sentryCache instanceof Promise) return this.sentryCache;else return Promise.resolve(this.sentryCache);
      } else {
        this.sentryCache = new Promise((res, rej) => {
          const sentryDsnUrl = this.config.get('error.sentry.' + (this.platformIsAdmin ? 'adminDSN' : 'frontendDSN'), false);
          if (sentryDsnUrl && !this.isSSR) {
            try {
              import('@sentry/browser').then(Sentry => {
                Sentry.init({
                  dsn: sentryDsnUrl,
                  environment: IS_PRODUCTION ? 'production' : 'development',
                  release: this.config.get('version'),
                  beforeSend: (event, hint) => this.processSentryEventBeforeSend(event, hint)
                });
                this.sentryCache = Sentry;
                // console.log('sentry initialized.')
                res(Sentry);
              });
            } catch (err) {
              throw new Error('failed to load sentry: ' + err);
            }
          } else {
            console.log('sentry not available because no dsn was specified in config.');
            res(null);
          }
        });
        return this.sentryCache;
      }
    }
    _handleData(incomingError, type) {
      const error = this.extractInformationFromIncomingData(incomingError);
      if (!error) return;
      if (type === 'error') {
        console.error(error);
      } else {
        console.log(error);
      }
      if (isPlatformServer(PLATFORM_ID)) return;
      if (type === 'info') return;
      // console.log('load sentry and pass error')
      this.loadSentry().then(sentry => {
        if (!sentry) return;
        sentry.withScope(scope => {
          this.addUserDetails(scope, type);
          if (typeof error === 'string') {
            sentry.captureMessage(error);
          } else if (error instanceof HttpErrorResponse) {
            scope.setContext('http_error', {
              serialized: error
            });
            sentry.captureEvent(httpErrorToCustomEvent(error));
          } else {
            sentry.captureException(error);
          }
          // sentry.showReportDialog({  });
        });
      });
    }
    addUserDetails(scope, type) {
      scope.setLevel(type);
      scope.setUser({
        ip: null
      });
      // scope.setTag("my-tag", "my value");
      const abilities = this.access.abilities;
      const role = this.access.role;
      const sessionId = this.access.sessionId;
      if (sessionId) {
        const anonymizedId = sessionId.substring(0, 8);
        scope.setUser({
          id: '' + anonymizedId,
          abilities,
          role,
          ip: null
        });
      }
    }
    // there are many possiblities of what is being passed.
    // common ones are Errors, HttpErrorResponses and AppMessages, which MAY wrap an error or not. 
    // if no error is being found, it will return null.
    extractInformationFromIncomingData(error) {
      if (!error) return null;
      if (error instanceof AppMessage) {
        return error.isError ? error.getError() : null;
      }
      if (error.originalError) return error.originalError;
      return error;
    }
    // NOTE: returning undefined will abort sending data.
    processSentryEventBeforeSend(event, hint) {
      const err = hint.originalException;
      // if _handleData spots an HttpErrorResponse, it will capture it as a lower-priority event instead of as exception.
      // so originalException should never be an HttpErrorResponse actually. Just in case, some time ago an additional filter was added.
      if (err instanceof HttpErrorResponse) {
        return;
      }
      // try {
      // 	if(err instanceof HttpErrorResponse) {
      // 		const message = err.error ? err.error.message : err.message;
      // 		event.exception.values[0].type = 'HttpErrorResponse: '+message;
      // 	}
      // 	console.log('beforesend',event,hint)
      // } catch (Oo) {
      // 	return event;
      // }
      return event;
    }
    static {
      this.ɵfac = function ErrorHandlerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ErrorHandlerService)(i0.ɵɵinject(i1.ConfigService), i0.ɵɵinject(i2.AccessService));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: ErrorHandlerService,
        factory: ErrorHandlerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ErrorHandlerService;
})();